import Swal from 'sweetalert2';
import applyStudentService from '@/services/applyStudent.js';
import { fileToBase64 } from './fileHelper.js';

/**
 * 上傳檔案並顯示進度
 * @param {Object} options - 上傳選項
 * @param {number} options.asId - 申請表ID
 * @param {Array} options.files - 要上傳的檔案陣列 [{file: File, pId: number, afId: number}]
 * @param {number} options.batchSize - 每批上傳的檔案數量 (預設: 3)
 * @returns {Promise<Object>} 上傳結果
 */
export async function uploadFilesWithProgress(options) {
  const { asId, files, batchSize = 3 } = options;

  if (!files || files.length === 0) {
    throw new Error('No files to upload');
  }

  // 顯示進度對話框
  Swal.fire({
    title: 'Uploading Files',
    html: `
      <div style="text-align: left; padding: 20px;">
        <div style="margin-bottom: 15px;">
          <strong>Progress:</strong> <span id="upload-progress-text">0%</span>
        </div>
        <div style="background: #f0f0f0; border-radius: 10px; height: 30px; overflow: hidden; margin-bottom: 15px;">
          <div id="upload-progress-bar" style="background: linear-gradient(to right, #4CAF50, #45a049); height: 100%; width: 0%; transition: width 0.3s ease;"></div>
        </div>
        <div style="font-size: 14px; color: #666;">
          <span id="upload-status-text">Preparing to upload...</span>
        </div>
        <div style="margin-top: 10px; font-size: 13px; color: #999;">
          <span id="upload-details-text">0 / ${files.length} files</span>
        </div>
      </div>
    `,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => {
      Swal.showLoading();
    }
  });

  try {
    const totalBatches = Math.ceil(files.length / batchSize);
    let allResults = [];
    let successCount = 0;
    let failCount = 0;

    // 分批上傳
    for (let i = 0; i < files.length; i += batchSize) {
      const batchFiles = files.slice(i, i + batchSize);
      const currentBatch = Math.floor(i / batchSize) + 1;
      const processedFiles = i;

      // 更新進度（批次開始）
      updateProgress({
        progress: (processedFiles / files.length) * 100,
        message: `Uploading batch ${currentBatch} of ${totalBatches}...`,
        details: `${processedFiles} / ${files.length} files`
      });

      // 準備批次上傳資料
      const uploadData = {
        asId: asId,
        files: await Promise.all(batchFiles.map(async (fileItem) => ({
          pId: fileItem.pId || 0,
          afId: fileItem.afId,
          fileName: fileItem.file.name,
          base64Data: await fileToBase64(fileItem.file)
        })))
      };

      // 上傳當前批次
      const result = await applyStudentService.uploadFiles(uploadData);

      if (result.success && result.data && result.data.results) {
        allResults = allResults.concat(result.data.results);
        
        // 統計成功和失敗數量
        result.data.results.forEach(r => {
          if (r.success) successCount++;
          else failCount++;
        });
      } else {
        // 批次失敗，標記所有檔案為失敗
        failCount += batchFiles.length;
        allResults = allResults.concat(
          batchFiles.map(f => ({
            afId: f.afId,
            success: false,
            message: result.message || 'Upload failed'
          }))
        );
      }

      // 更新進度（批次完成）
      const completedFiles = Math.min(i + batchSize, files.length);
      updateProgress({
        progress: (completedFiles / files.length) * 100,
        message: `Completed batch ${currentBatch} of ${totalBatches}`,
        details: `${completedFiles} / ${files.length} files`
      });

      // 短暫延遲，讓使用者看到進度更新
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    // 上傳完成
    Swal.close();

    // 顯示結果摘要
    await showUploadResult({
      totalFiles: files.length,
      successCount,
      failCount,
      results: allResults
    });

    return {
      success: successCount > 0,
      totalFiles: files.length,
      successCount,
      failCount,
      results: allResults
    };

  } catch (error) {
    Swal.close();
    
    await Swal.fire({
      icon: 'error',
      title: 'Upload Error',
      text: error.message || 'An unexpected error occurred during upload',
      confirmButtonText: 'OK'
    });

    throw error;
  }
}

/**
 * 更新進度顯示
 */
function updateProgress({ progress, message, details }) {
  const progressBar = document.getElementById('upload-progress-bar');
  const progressText = document.getElementById('upload-progress-text');
  const statusText = document.getElementById('upload-status-text');
  const detailsText = document.getElementById('upload-details-text');

  if (progressBar) {
    progressBar.style.width = `${Math.round(progress)}%`;
  }
  if (progressText) {
    progressText.textContent = `${Math.round(progress)}%`;
  }
  if (statusText) {
    statusText.textContent = message;
  }
  if (detailsText) {
    detailsText.textContent = details;
  }
}

/**
 * 顯示上傳結果摘要
 */
async function showUploadResult({ totalFiles, successCount, failCount, results }) {
  const hasFailures = failCount > 0;
  
  let resultHtml = `
    <div style="text-align: left; padding: 20px;">
      <div style="margin-bottom: 20px; font-size: 16px;">
        <strong>Upload Summary:</strong>
      </div>
      <div style="margin-bottom: 15px;">
        <span style="color: #4CAF50;">✓ Successful: ${successCount}</span><br>
        ${hasFailures ? `<span style="color: #f44336;">✗ Failed: ${failCount}</span>` : ''}
      </div>
  `;

  // 如果有失敗的檔案，顯示失敗詳情
  if (hasFailures) {
    resultHtml += `
      <div style="margin-top: 20px; max-height: 200px; overflow-y: auto; border: 1px solid #ddd; padding: 10px; border-radius: 5px; background: #f9f9f9;">
        <strong style="color: #f44336;">Failed Files:</strong>
        <ul style="margin: 10px 0; padding-left: 20px; text-align: left;">
    `;
    
    results.filter(r => !r.success).forEach(result => {
      resultHtml += `<li style="margin: 5px 0; font-size: 13px;">File ID ${result.afId}: ${result.message}</li>`;
    });
    
    resultHtml += `
        </ul>
      </div>
    `;
  }

  resultHtml += `</div>`;

  await Swal.fire({
    icon: hasFailures ? 'warning' : 'success',
    title: hasFailures ? 'Upload Completed with Errors' : 'Upload Completed Successfully',
    html: resultHtml,
    confirmButtonText: 'OK',
    width: '600px'
  });
}

/**
 * 上傳單一檔案（簡化版）
 * @param {Object} options - 上傳選項
 * @param {number} options.asId - 申請表ID
 * @param {File} options.file - 要上傳的檔案
 * @param {number} options.pId - 學程ID (0 表示通用文件)
 * @param {number} options.afId - 申請檔案類型ID
 * @returns {Promise<Object>} 上傳結果
 */
export async function uploadSingleFile(options) {
  const { asId, file, pId = 0, afId } = options;

  return uploadFilesWithProgress({
    asId,
    files: [{ file, pId, afId }],
    batchSize: 1
  });
}
